/* Cable chain drawing for the necklace builder: alternating face-on and edge-on links along the
   builder's chain curve, plus jump rings. Returns SVG markup strings, so the same code runs in the
   browser (builder) and in Node (email chain images). No dependencies. */
(function (root) {
  // Link sizes per layout, in px. pitch = distance between link centers along the chain.
  var SIZES = {
    desktop: { length: 7.2, width: 4.4, wire: 1.15, pitch: 5.6, ringWire: 1.7 },
    mobile: { length: 4.4, width: 2.7, wire: 0.8, pitch: 3.4, ringWire: 1.2 },
    // Email chain images display at 1x (email apps can't scale background images), so slightly bolder.
    email: { length: 4.6, width: 3, wire: 1, pitch: 3.6, ringWire: 1.2 },
  };

  // Three tones per finish: outline, metal body, highlight. Sampled from the charm art's own metal
  // (neutral gray silver, honey gold) so the chain matches the charms.
  var TONES = {
    silver: { edge: '#939393', body: '#CDCDCD', shine: '#F4F4F4' },
    gold: { edge: '#C3953A', body: '#F3CA6C', shine: '#FFECBA' },
  };

  function fmt(n) { return Math.round(n * 100) / 100; }

  // Sample the quadratic curve M(x0,y0) Q(cx,cy) (x1,y1) into points with cumulative length.
  function sample(c, steps) {
    var pts = [];
    var len = 0;
    for (var i = 0; i <= steps; i++) {
      var t = i / steps;
      var mt = 1 - t;
      var x = mt * mt * c.x0 + 2 * mt * t * c.cx + t * t * c.x1;
      var y = mt * mt * c.y0 + 2 * mt * t * c.cy + t * t * c.y1;
      if (i) len += Math.hypot(x - pts[i - 1].x, y - pts[i - 1].y);
      pts.push({ x: x, y: y, s: len });
    }
    return pts;
  }

  // Point and unit tangent at arc length s.
  function at(pts, s) {
    var lo = 0;
    var hi = pts.length - 1;
    while (hi - lo > 1) {
      var mid = (lo + hi) >> 1;
      if (pts[mid].s < s) lo = mid; else hi = mid;
    }
    var a = pts[lo];
    var b = pts[hi];
    var f = b.s === a.s ? 0 : (s - a.s) / (b.s - a.s);
    var dx = b.x - a.x;
    var dy = b.y - a.y;
    var d = Math.hypot(dx, dy) || 1;
    return { x: a.x + dx * f, y: a.y + dy * f, ux: dx / d, uy: dy / d };
  }

  function layer(d, color, width) {
    return d ? '<path d="' + d + '" fill="none" stroke="' + color + '" stroke-width="' + fmt(width) + '" stroke-linecap="round"/>' : '';
  }

  // curve: { x0, y0, cx, cy, x1, y1 }, finish: 'silver' | 'gold', size: 'desktop' | 'mobile' | 'email'
  function links(curve, finish, size) {
    var z = SIZES[size] || SIZES.desktop;
    var tone = TONES[finish] || TONES.silver;
    var pts = sample(curve, 240);
    var total = pts[pts.length - 1].s;
    var a = z.length / 2;
    var b = z.width / 2;
    var faceOutline = '';
    var faceShine = '';
    var sideOutline = '';
    var sideShine = '';
    var k = 0;
    for (var s = z.pitch / 2; s < total; s += z.pitch, k++) {
      var p = at(pts, s);
      var deg = fmt(Math.atan2(p.uy, p.ux) * 180 / Math.PI);
      var lx = fmt(p.x - a * p.ux);
      var ly = fmt(p.y - a * p.uy);
      var rx = fmt(p.x + a * p.ux);
      var ry = fmt(p.y + a * p.uy);
      if (k % 2 === 0) {
        // Face-on link: an oval ring. The highlight is its upper half, drawn slightly inside.
        faceOutline += 'M' + lx + ',' + ly + 'A' + a + ',' + b + ' ' + deg + ' 0 1 ' + rx + ',' + ry + 'A' + a + ',' + b + ' ' + deg + ' 0 1 ' + lx + ',' + ly;
        var ia = a * 0.7;
        var ib = b * 0.62;
        faceShine += 'M' + fmt(p.x - ia * p.ux) + ',' + fmt(p.y - ia * p.uy) + 'A' + fmt(ia) + ',' + fmt(ib) + ' ' + deg + ' 0 1 ' + fmt(p.x + ia * p.ux) + ',' + fmt(p.y + ia * p.uy);
      } else {
        // Edge-on link: seen side on, it reads as a short bar.
        sideOutline += 'M' + lx + ',' + ly + 'L' + rx + ',' + ry;
        var off = z.wire * 0.25;
        sideShine += 'M' + fmt(p.x - a * 0.6 * p.ux + off * p.uy) + ',' + fmt(p.y - a * 0.6 * p.uy - off * p.ux) + 'L' + fmt(p.x + a * 0.6 * p.ux + off * p.uy) + ',' + fmt(p.y + a * 0.6 * p.uy - off * p.ux);
      }
    }
    return layer(faceOutline, tone.edge, z.wire * 1.25)
      + layer(faceOutline, tone.body, z.wire * 0.6)
      + layer(faceShine, tone.shine, z.wire * 0.35)
      + layer(sideOutline, tone.edge, z.wire * 1.9)
      + layer(sideOutline, tone.body, z.wire * 1.05)
      + layer(sideShine, tone.shine, z.wire * 0.4);
  }

  // Jump rings: rings = [{ cx, cy, r }]
  function rings(list, finish, size) {
    var z = SIZES[size] || SIZES.desktop;
    var tone = TONES[finish] || TONES.silver;
    var outline = '';
    var shine = '';
    list.forEach(function (c) {
      var r = fmt(c.r);
      outline += 'M' + fmt(c.cx - c.r) + ',' + fmt(c.cy) + 'a' + r + ',' + r + ' 0 1,0 ' + fmt(2 * c.r) + ',0a' + r + ',' + r + ' 0 1,0 ' + fmt(-2 * c.r) + ',0';
      var ir = c.r * 0.8;
      shine += 'M' + fmt(c.cx - ir * 0.7) + ',' + fmt(c.cy - ir * 0.7) + 'A' + fmt(ir) + ',' + fmt(ir) + ' 0 0 1 ' + fmt(c.cx + ir * 0.7) + ',' + fmt(c.cy - ir * 0.7);
    });
    return layer(outline, tone.edge, z.ringWire * 1.3)
      + layer(outline, tone.body, z.ringWire * 0.65)
      + layer(shine, tone.shine, z.ringWire * 0.35);
  }

  var api = { links: links, rings: rings, SIZES: SIZES, TONES: TONES };
  root.NecklaceChain = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
